import {BounceRole} from "@bounce/shared/consumer/declarations";
import React, {PropsWithChildren, useEffect, useRef} from "react";
import {matchPath, useLocation, useNavigate} from "react-router-dom";
import {
  KEY_OPEN_CANCEL_PLAN_DIALOG,
  KEY_OPEN_DETAILS_DIALOG,
  KEY_OPEN_DETAILS_DIALOG_DASHBOARD,
  KEY_OPEN_MANAGE_CONTACT_DIALOG,
} from "~/constants/no-login-keys";
import {useAuth} from "@bounce/shared/contexts";
import {isValueFromStorageTrue, useLoginDestinationUrl, useConsumerUser} from "~/contexts";
import {urls} from "~/routes/consumerUrls";

interface BlockableGuardProps extends PropsWithChildren {
  allowNoLogin?: boolean;
}

const AuthRoleGuard = ({allowNoLogin = false, children}: BlockableGuardProps) => {
  const {userRole, userId, loggedIn} = useAuth();
  const {preLoginContext} = useConsumerUser();
  const {pathname, search} = useLocation();
  const {setDestinationUrl} = useLoginDestinationUrl();
  const navigate = useNavigate();
  const hasRedirectedRef = useRef(false);

  const allowedRoles = [BounceRole.USER, ...(allowNoLogin ? [BounceRole.NO_LOGIN_USER] : [])];
  const noPermissions = !userRole || !allowedRoles.includes(userRole);

  // Dialog flags
  const openDetailsDialog = isValueFromStorageTrue(KEY_OPEN_DETAILS_DIALOG);
  const openManageContactDialog = isValueFromStorageTrue(KEY_OPEN_MANAGE_CONTACT_DIALOG);
  const openCancelPlanDialog = isValueFromStorageTrue(KEY_OPEN_CANCEL_PLAN_DIALOG);
  const openDetailsDialogDashboard = isValueFromStorageTrue(KEY_OPEN_DETAILS_DIALOG_DASHBOARD);

  const hasOpenAccountDialogs = openDetailsDialog || openManageContactDialog;
  const hasOpenDashboardDialogs = openCancelPlanDialog || openDetailsDialogDashboard;
  const hasOpenDialogFlags = hasOpenAccountDialogs || hasOpenDashboardDialogs;

  const isAccountMainPath = matchPath({path: "/account/:accountId"}, pathname);
  const isDashboardPath = matchPath({path: "/account/:accountId/account-dashboard"}, pathname);
  const isAccountsPath = pathname === urls.accounts.url;
  const isNoLoginWithAccountContext = userRole === BounceRole.NO_LOGIN_USER && !!preLoginContext?.account_id;

  const isOnAllowedPage =
    (isAccountMainPath && !hasOpenAccountDialogs) ||
    (isDashboardPath && userRole === BounceRole.NO_LOGIN_USER && !hasOpenDashboardDialogs) ||
    (isAccountsPath && isNoLoginWithAccountContext);

  const shouldRedirect = !loggedIn && !isOnAllowedPage && (hasOpenDialogFlags || noPermissions);

  useEffect(() => {
    if (!noPermissions) {
      hasRedirectedRef.current = false;
    }
  }, [noPermissions]);

  const redirectToLogin = () => {
    if (hasRedirectedRef.current) return;

    hasRedirectedRef.current = true;
    setDestinationUrl(pathname + search);

    const userIdQuery = userId ? `?id=${userId}` : "";
    navigate(urls.login.url + userIdQuery, {
      state: {
        globalLogin: true,
        verifyIdentity: true,
      },
    });
  };

  useEffect(() => {
    if (shouldRedirect) {
      redirectToLogin();
    }
  }, [shouldRedirect]);

  if (shouldRedirect) {
    return null;
  }

  return <>{children}</>;
};

export default AuthRoleGuard;
